// D37.2: the project's dependency install ("prepare") command, per workspace, where a PERSON sets it: the suggestion
// detected from the checkout's lockfile (never applied by itself), the install-scripts opt-in (off by default), what the
// worker's sandbox offers (registry proxy verified, package managers cached in the image) and the last install
// outcome. `/projects/{id}/settings?field=prepare_command` (the assistant's open_settings_form proposal) focuses it.
// Saved through the project PATCH from the signed-in session (an API token is refused: prepare_session_only).
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import type { ProjectSummary } from "@maestros/contracts";
import { useI18n } from "../../i18n/index.tsx";
import type { MessageKey } from "../../i18n/en.ts";
import { getDependencySetup, savePrepareCommands, type WorkspaceDependencySetup } from "../../api/endpoints-dependencies.ts";
import type { WorkspaceRow } from "../../api/endpoints-blocks.ts";
import { isApiError } from "../../api/client.ts";
import { useAsync } from "../../lib/useAsync.ts";
import { Button } from "../../components/Button.tsx";
import { Banner, localErrorMessage } from "../../components/Feedback.tsx";
import { TextField } from "../../components/Field.tsx";
import { Pill } from "../../components/Status.tsx";
import { useToast } from "../../components/Toast.tsx";
import { useProjectRole } from "../wave3/common.tsx";

export interface DependencyValue {
  prepare: string;
  allowScripts: boolean;
}

export interface DependencyPanelProps {
  workspaces: readonly WorkspaceRow[];
  setup: readonly WorkspaceDependencySetup[] | null;
  canEdit: boolean;
  focus: boolean;
  onSave: (values: DependencyValue[]) => Promise<void>;
}

function initialValues(workspaces: readonly WorkspaceRow[]): DependencyValue[] {
  return workspaces.map((w) => ({ prepare: typeof w.commands["prepare"] === "string" ? (w.commands["prepare"] as string) : "", allowScripts: w.commands["allow_install_scripts"] === true }));
}

/** Presentational panel (tested without the network). */
export function DependencyPanel({ workspaces, setup, canEdit, focus, onSave }: DependencyPanelProps) {
  const { t, formatRelative } = useI18n();
  const key = JSON.stringify(initialValues(workspaces));
  const [values, setValues] = useState<DependencyValue[]>(() => initialValues(workspaces));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const firstRef = useRef<HTMLInputElement>(null);
  useEffect(() => setValues(JSON.parse(key) as DependencyValue[]), [key]);
  useEffect(() => {
    if (!focus || !firstRef.current) return;
    firstRef.current.scrollIntoView({ block: "center" });
    firstRef.current.focus();
  }, [focus, workspaces]);
  const dirty = JSON.stringify(values) !== key;
  const set = (i: number, patch: Partial<DependencyValue>) => setValues((v) => v.map((x, k) => (k === i ? { ...x, ...patch } : x)));
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await onSave(values);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card card--pad stack" aria-labelledby="dependencies-title" data-testid="dependencies-card">
      <h2 id="dependencies-title" className="card-title">
        {t("deps.title")}
      </h2>
      <p className="muted">{t("deps.body")}</p>
      {workspaces.map((w, i) => {
        const facts = setup?.find((s) => s.root_path === w.root_path && s.subpath === w.subpath) ?? null;
        const suggestion = facts?.suggestions[0] ?? null;
        const pms = facts?.package_managers ?? null;
        const pmList = pms ? [...pms.pnpm.map((v) => `pnpm ${v}`), ...pms.yarn.map((v) => `yarn ${v}`)] : [];
        return (
          <div key={`${w.runner_id}:${w.root_path}:${w.subpath}`} className="stack" data-testid={`dependencies-workspace-${i}`}>
            <TextField
              ref={i === 0 ? firstRef : undefined}
              label={t("deps.workspace", { path: `${w.root_path}${w.subpath ? `/${w.subpath}` : ""}` })}
              hint={t("deps.hint")}
              value={values[i]?.prepare ?? ""}
              onChange={(e) => set(i, { prepare: e.target.value })}
              placeholder={suggestion?.command ?? "pnpm install --frozen-lockfile"}
              spellCheck={false}
              className="mono"
              maxLength={2000}
              disabled={!canEdit || busy}
              data-testid={`prepare-command-${i}`}
            />
            {suggestion && (values[i]?.prepare ?? "").trim() !== suggestion.command ? (
              <div className="row row--wrap">
                <span className="subtle" data-testid={`prepare-suggestion-${i}`}>
                  {t("deps.suggested", { lockfile: suggestion.lockfile, command: suggestion.command })}
                </span>
                {canEdit ? (
                  <Button size="sm" onClick={() => set(i, { prepare: suggestion.command })} disabled={busy} data-testid={`prepare-use-suggestion-${i}`}>
                    {t("deps.useSuggestion")}
                  </Button>
                ) : null}
              </div>
            ) : null}
            {facts === null || facts.worker === null ? <p className="field-hint">{t("deps.unknownFacts")}</p> : facts.lockfiles.length === 0 ? <p className="field-hint">{t("deps.noLockfile")}</p> : null}
            <label className="choice">
              <input type="checkbox" checked={values[i]?.allowScripts ?? false} onChange={(e) => set(i, { allowScripts: e.target.checked })} disabled={!canEdit || busy} data-testid={`prepare-allow-scripts-${i}`} />
              <span>
                {t("deps.allowScripts")}
                <span className="field-hint" style={{ display: "block" }}>
                  {t("deps.allowScriptsHint")}
                </span>
              </span>
            </label>
            {facts ? (
              <ul className="plain-list" aria-label={t("deps.title")}>
                <li>
                  <Pill tone={facts.registry_verified ? "success" : "warning"} icon={facts.registry_verified ? "check" : "circle"}>
                    {t(facts.registry_verified ? "deps.registryOk" : "deps.registryOff")}
                  </Pill>
                </li>
                <li className="subtle">{pms === null || pmList.length === 0 ? t("deps.cachedNone") : t("deps.cached", { list: pmList.join(", ") })}</li>
                {facts.package_manager ? <li className="subtle">{t("deps.asks", { pm: facts.package_manager })}</li> : null}
                {facts.last ? (
                  <li className="subtle" data-testid={`prepare-last-${i}`}>
                    {facts.last.status === "installed"
                      ? t("deps.lastInstalled", { when: formatRelative(facts.last.at) })
                      : t("deps.lastFailed", { when: formatRelative(facts.last.at), cause: t(`deps.cause.${facts.last.code ?? "prepare_failed"}` as MessageKey) })}
                  </li>
                ) : null}
              </ul>
            ) : null}
          </div>
        );
      })}
      {!canEdit ? <p className="field-hint">{t("deps.readOnly")}</p> : null}
      {error ? (
        <Banner tone="error" role="alert">
          {localErrorMessage(error, t)}
        </Banner>
      ) : null}
      {workspaces.length > 0 ? (
        <div className="row">
          <Button variant="primary" size="sm" loading={busy} disabled={!dirty || !canEdit} onClick={() => void save()} data-testid="dependencies-save">
            {t("common.save")}
          </Button>
        </div>
      ) : null}
    </section>
  );
}

/** The card on the project settings tab: loads the facts, saves through the project PATCH. */
export function DependenciesCard({ project, workspaces, onSaved }: { project: ProjectSummary; workspaces: readonly WorkspaceRow[] | undefined; onSaved: () => void }) {
  const { t } = useI18n();
  const { toast } = useToast();
  const canEdit = useProjectRole(project.id).atLeast("maintainer");
  const [params] = useSearchParams();
  const setup = useAsync((s) => getDependencySetup(project.id, s), [project.id, project.version]);
  if (!workspaces || workspaces.length === 0) return null;
  const onSave = async (values: DependencyValue[]) => {
    try {
      await savePrepareCommands(project.id, project.version, workspaces, values);
    } catch (e) {
      if (isApiError(e) && e.code === "version_conflict") onSaved();
      throw e;
    }
    toast(t("deps.saved"), "success");
    onSaved();
  };
  return <DependencyPanel workspaces={workspaces} setup={setup.data?.workspaces ?? null} canEdit={canEdit} focus={params.get("field") === "prepare_command"} onSave={onSave} />;
}
